import { useEffect, useRef } from 'react'
import fetchItemPolicyEvaluations from 'actions/businessTraveller/fetchItemPolicyEvaluations'
import useItemPolicyCacheKey from 'businessTraveller/hooks/itemPolicy/useItemPolicyCacheKey'
import useTripRequestId from 'businessTraveller/hooks/useTripRequestId'
import { logItemPolicyComparison, type ItemPolicyCompareSurface, type ItemPolicyCompareVertical } from 'businessTraveller/utils/logItemPolicyComparison'
import { hasHotelItemPolicyLocation } from 'businessTraveller/utils/resolveHotelItemPolicyLocation'
import { useAppDispatch } from 'hooks/reduxHooks'

/**
 * Payload for {@link useItemPolicyShadowFetcher}.
 * `legacyData` / `itemPolicyData` are whatever each code path stores for this cache key (often Redux async blobs).
 */
interface Params {
  enabled: boolean
  businessId?: string
  cacheKey: string
  item: App.BusinessTraveller.ItemPolicyEvaluationItem | undefined
  employeeIds: Array<string>
  vertical: ItemPolicyCompareVertical
  surface: ItemPolicyCompareSurface
  legacyData: unknown
  itemPolicyData: unknown
}

/**
 * Runs the **item-policy calculated-credits** request in parallel with legacy flows so we can validate the new stack
 * during rollout without switching UX to it yet (“shadow” traffic).
 *
 * When both legacy and item-policy results are available for the same key, emits a single Datadog comparison event
 * ({@link logItemPolicyComparison}) for dashboards / debugging—not for driving UI.
 *
 * Gate with `enabled` so surfaces opt in only where shadow traffic is desired (e.g. budget bars, offer credits).
 */
export function useItemPolicyShadowFetcher(params: Params): void {
  const {
    enabled,
    businessId,
    cacheKey,
    item,
    employeeIds,
    vertical,
    surface,
    legacyData,
    itemPolicyData,
  } = params
  const appDispatch = useAppDispatch()
  const tripRequestId = useTripRequestId()
  const resolvedCacheKey = useItemPolicyCacheKey(cacheKey)
  const hasLoggedRef = useRef(false)
  const itemRef = useRef(item)
  itemRef.current = item

  const employeeIdsKey = employeeIds.join(',')

  useEffect(() => {
    const shadowItem = itemRef.current
    if (
      !enabled ||
      !businessId ||
      !cacheKey ||
      !shadowItem ||
      employeeIds.length === 0 ||
      (shadowItem.itemPolicyType === 'HOTEL' && !hasHotelItemPolicyLocation(shadowItem.location))
    ) {
      return
    }

    appDispatch(fetchItemPolicyEvaluations([resolvedCacheKey, shadowItem, employeeIds, tripRequestId], businessId))
  // employeeIdsKey tracks employeeIds content without unstable array reference churn.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDispatch, tripRequestId, businessId, resolvedCacheKey, enabled, employeeIdsKey, surface, vertical])

  useEffect(() => {
    if (!enabled || hasLoggedRef.current) {
      return
    }

    const hasStatusSuccess = (data: unknown): boolean =>
      typeof data === 'object' &&
      data !== null &&
      'status' in data &&
      (data as { status?: string }).status === 'success'

    const legacyReady = legacyData != null && (
      typeof legacyData !== 'object' || !('status' in legacyData) || hasStatusSuccess(legacyData)
    )
    const itemPolicyReady = itemPolicyData != null && (
      typeof itemPolicyData !== 'object' || !('status' in itemPolicyData) || hasStatusSuccess(itemPolicyData)
    )

    if (legacyReady && itemPolicyReady) {
      hasLoggedRef.current = true
      logItemPolicyComparison({
        vertical,
        surface,
        businessId,
        cacheKey,
        employeeIds,
        old: legacyData,
        new: itemPolicyData,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, cacheKey, employeeIdsKey, enabled, itemPolicyData, legacyData, surface, vertical])
}
